import {
  CardStackMinusIcon,
  DashboardIcon,
  Pencil2Icon,
  TextAlignRightIcon,
  UploadIcon,
} from "@radix-ui/react-icons";
import { NotebookPen, Youtube } from "lucide-react";
import { AnkiIcon } from "@/components/ui/icons";
import { useI18n } from "@/locales/client";

interface Feature {
  text: string;
  footnote?: string;
  negative?: boolean;
}

interface PlanTagline {
  yearly: string;
  monthly: string;
}

interface PricingItem {
  plan: string;
  tagline: PlanTagline;
  quota: number;
  price: {
    yearly: number;
    monthly: number;
  };
  features: Feature[];
}

export const usePricingItems = (): PricingItem[] => {
  const t = useI18n();

  return [
    {
      plan: "Free",
      tagline: {
        yearly: t("pricing.free.tagline.yearly"),
        monthly: t("pricing.free.tagline.monthly"),
      },
      quota: 10,
      price: {
        yearly: 0,
        monthly: 0,
      },
      features: [
        {
          text: t("pricing.free.features.manualCreate.text"),
          footnote: t("pricing.free.features.manualCreate.footnote"),
        },
        {
          text: t("pricing.free.features.progressTracking.text"),
          footnote: t("pricing.free.features.progressTracking.footnote"),
        },
        {
          text: t("pricing.free.features.fileSizeLimit.text"),
          footnote: t("pricing.free.features.fileSizeLimit.footnote"),
        },
        // {
        //   text: "Import from Quizlet",
        //   footnote: "Easily import your existing Quizlet study sets.",
        // },
        // {
        //   text: "Import from Anki",
        //   footnote: "Import your Anki decks for seamless transition.",
        // },
        {
          text: t("pricing.free.features.aiFlashcards.text"),
          footnote: t("pricing.free.features.aiFlashcards.footnote"),
          negative: true,
        },
        {
          text: t("pricing.free.features.aiMultipleChoice.text"),
          footnote: t("pricing.free.features.aiMultipleChoice.footnote"),
          negative: true,
        },
        {
          text: t("pricing.free.features.aiWrittenQuizzes.text"),
          footnote: t("pricing.free.features.aiWrittenQuizzes.footnote"),
          negative: true,
        },
        {
          text: t("pricing.free.features.aiTutorChats.text"),
          footnote: t("pricing.free.features.aiTutorChats.footnote"),
          negative: true,
        },
        {
          text: t("pricing.free.features.aiNotes.text"),
          footnote: t("pricing.free.features.aiNotes.footnote"),
          negative: true,
        },
        {
          text: t("pricing.free.features.audioStudy.text"),
          footnote: t("pricing.free.features.audioStudy.footnote"),
          negative: true,
        },
      ],
    },
    {
      plan: "Premium",
      tagline: {
        yearly: t("pricing.premium.tagline.yearly"),
        monthly: t("pricing.premium.tagline.monthly"),
      },
      quota: 50,
      price: {
        yearly: 99.99,
        monthly: 14.99,
      },
      features: [
        {
          text: t("pricing.premium.features.manualCreate.text"),
        },
        {
          text: t("pricing.premium.features.progressTracking.text"),
          footnote: t("pricing.premium.features.progressTracking.footnote"),
        },
        {
          text: t("pricing.premium.features.fileSizeLimit.text"),
          footnote: t("pricing.premium.features.fileSizeLimit.footnote"),
        },
        // {
        //   text: "Import from Quizlet",
        //   footnote:
        //     "Seamlessly import and enhance your Quizlet study sets with AI features.",
        // },
        // {
        //   text: "Import from Anki",
        //   footnote:
        //     "Import Anki decks and supercharge them with AI-powered learning tools.",
        // },
        {
          text: t("pricing.premium.features.aiFlashcards.text"),
          footnote: t("pricing.premium.features.aiFlashcards.footnote"),
        },
        {
          text: t("pricing.premium.features.aiMultipleChoice.text"),
          footnote: t("pricing.premium.features.aiMultipleChoice.footnote"),
        },
        {
          text: t("pricing.premium.features.aiWrittenQuizzes.text"),
          footnote: t("pricing.premium.features.aiWrittenQuizzes.footnote"),
        },
        {
          text: t("pricing.premium.features.aiTutorChats.text"),
          footnote: t("pricing.premium.features.aiTutorChats.footnote"),
        },
        {
          text: t("pricing.premium.features.aiNotes.text"),
          footnote: t("pricing.premium.features.aiNotes.footnote"),
        },
        {
          text: t("pricing.premium.features.audioStudy.text"),
          footnote: t("pricing.premium.features.audioStudy.footnote"),
        },
      ],
    },
  ];
};

export const aiFeatures = [
  {
    title: "Unlimited AI file generations",
  },
  {
    title: "Unlimited AI video generations",
  },
  {
    title: "Unlimited AI tutor chats",
  },
  {
    title: "Unlimited AI file upload size",
  },
];

export const OPTIONS = [
  {
    icon: <CardStackMinusIcon className="size-4 text-[#0077FF]" />,
    title: "Flashcards",
    description: "The flashcards you know and love.",
  },
  {
    icon: <DashboardIcon className="size-4 text-[#0077FF]" />,
    title: "Multiple Choice Questions",
    description: "Simulate exam conditions.",
  },
  {
    icon: <Pencil2Icon className="size-4 text-[#0077FF]" />,
    title: "Written Questions",
    description: "The true test of your knowledge.",
  },
];

export const UPLOAD_METHODS = [
  {
    title: "File Upload",
    value: "file_upload",
    icon: <UploadIcon className="w-4 h-4" />,
    active: true,
  },
  {
    title: "Create Manually",
    value: "create_manually",
    icon: <Pencil2Icon className="w-4 h-4" />,
    active: true,
  },
  {
    title: "Paste Text",
    value: "paste_text",
    icon: <TextAlignRightIcon className="w-4 h-4" />,
    active: true,
  },
  {
    title: "Youtube Video",
    value: "youtube_url",
    icon: <Youtube className="w-4 h-4" />,
    active: true,
  },
  {
    title: "Ai Notetaker",
    value: "ai_notetaker",
    icon: <NotebookPen className="w-4 h-4" />,
    active: true,
  },
  {
    title: "Import from Anki",
    value: "anki_import",
    icon: <AnkiIcon className="w-4 h-4" />,
    active: true,
  },
] as const;

// Type for the upload method
export type UploadMethodValue = (typeof UPLOAD_METHODS)[number]["value"];

export type ImportMethod =
  | "file_upload"
  | "paste_text"
  | "youtube_url"
  | "anki_import";

export type StepKey = "chooseMethod" | "uploadContent" | "upgrade";

interface Step {
  step: number;
  key: StepKey;
  title: string;
  description: string;
}

export const getSteps = (uploadMethod: UploadMethodValue | null): Step[] => {
  const baseStep: Step = {
    step: 1,
    key: "chooseMethod",
    title: "How would you like to create your study set?",
    description: "Choose an input method to create your study set",
  };

  const uploadContentStep: Step = {
    step: 2,
    key: "uploadContent",
    title: "File Upload",
    description:
      "Studygenie's AI will convert your file into high-quality study material.",
  };

  const upgradeStep: Step = {
    step: 3,
    key: "upgrade",
    title:
      "Do you want questions from the entire file or just the first 1000 words?",
    description:
      "If you want to get questions from the entire file, upgrade to Studygenie Premium.",
  };

  if (uploadMethod === "file_upload") {
    return [baseStep, uploadContentStep, upgradeStep];
  }

  if (uploadMethod === "create_manually") {
    return [
      baseStep,
      {
        ...uploadContentStep,
        title: "Create your flashcards",
        description: "Add terms and definitions to create your flashcards.",
      },
    ];
  }

  if (uploadMethod === "paste_text") {
    return [
      baseStep,
      {
        ...uploadContentStep,
        title: "Paste Text",
        description:
          "Studygenie's AI will convert your text into high-quality study material.",
      },
      {
        ...upgradeStep,
        title:
          "Do you want questions from the entire pasted text or just the first 1000 words?",
        description:
          "If you want to get questions from the entire pasted text, upgrade to Studygenie Premium.",
      },
    ];
  }

  if (uploadMethod === "youtube_url") {
    return [
      baseStep,
      {
        ...uploadContentStep,
        title: "YouTube Video",
        description:
          "Studygenie's AI will convert the video into high-quality study material.",
      },
      {
        ...upgradeStep,
        title:
          "Do you want questions from the YouTube video or just the first 1000 words?",
        description:
          "If you want to get questions from the YouTube video, upgrade to Studygenie Premium.",
      },
    ];
  }

  if (uploadMethod === "anki_import") {
    return [
      baseStep,
      {
        ...uploadContentStep,
        title: "Import from Anki",
        description: "Upload your Anki (.apkg) file to import your flashcards.",
      },
    ];
  }

  return [
    baseStep,
    {
      ...uploadContentStep,
      title: "",
      description: "",
    },
    {
      ...upgradeStep,
      title: "",
      description: "",
    },
  ];
};
